import {
  Building2,
  CalendarClock,
  Circle,
  ClipboardCheck,
  FileCheck2,
  IdCard,
  KeyRound,
  MessageCircle,
  Phone,
  Trophy,
  UserPlus,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type { FilaDeCartera } from '@/api/cartera.api';
import { Avatar } from '@/components/ui/Avatar';
import { enlaceTel } from '@/lib/cartera';
import { cn } from '@/lib/cn';
import { PIPELINE_COLUMNS } from '@/lib/crm';
import { datosDe, enEtapaDesde, faltan, pasosDe, plazoDe, posicionDe, type Plazo } from '@/lib/proceso';

/**
 * LA TARJETA DEL CLIENTE CON SU LÍNEA DE PROGRESO (tanda 6; la captura 1 de Kervens).
 *
 * Dice de un vistazo en qué paso del proceso está, qué sigue y cuánto falta para cerrar.
 * Es azul marino en tema claro y en tema oscuro, a propósito: es la única pieza del panel
 * que no usa el violeta de marca. Sin estado: se prueba con renderToStaticMarkup.
 */
export interface TarjetaDeProgresoProps {
  fila: FilaDeCartera;
  etapas: readonly string[];
  ahora: Date;
  /** La que encabeza la ventana de la ficha: sin sombra exterior ni botón «Ver ficha». */
  compacta?: boolean;
  onVerFicha?: () => void;
  /** Si no viene, el botón Chat no se dibuja. */
  onAbrirChat?: () => void;
}

/** `Paso.icono` es un nombre (lib/proceso.ts es PURO); aquí se vuelve el componente. */
const ICONOS: Record<string, LucideIcon> = { UserPlus, ClipboardCheck, CalendarClock, Video, Building2, FileCheck2, KeyRound, Trophy, Circle };

// Casi negro arriba, azul eléctrico abajo, y una luz que sube desde el borde inferior.
const FONDO =
  'radial-gradient(120% 80% at 50% 118%, rgba(37, 99, 235, 0.55) 0%, rgba(37, 99, 235, 0) 62%), ' +
  'radial-gradient(60% 50% at 88% -12%, rgba(56, 189, 248, 0.14) 0%, rgba(56, 189, 248, 0) 70%), ' +
  'linear-gradient(180deg, #050816 0%, #0a1230 52%, #0f2a78 100%)';

const TONO: Record<Plazo['tono'], string> = {
  normal: 'text-sky-100/80',
  alerta: 'text-amber-300',
  apagado: 'text-sky-100/60',
};

// Los apagados llevan fondo opaco: la línea pasa por detrás de todos los nodos.
const NODO = {
  hecho: 'bg-blue-600 text-white',
  actual: 'bg-blue-500 text-white shadow-[0_0_0_4px_rgba(59,130,246,0.22),0_6px_18px_-2px_rgba(59,130,246,0.9)]',
  falta: 'bg-[#101a3c] text-white/50 ring-1 ring-inset ring-white/10',
} as const;

const PILDORA =
  'inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-white/10 px-3.5 ' +
  'text-[13px] font-medium text-white ring-1 ring-inset ring-white/15 transition-colors hover:bg-white/20 active:bg-white/25 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300 md:min-h-[36px]';

export function TarjetaDeProgreso({ fila, etapas, ahora, compacta = false, onVerFicha, onAbrirChat }: TarjetaDeProgresoProps) {
  const pasos = pasosDe(etapas);
  const posicion = posicionDe(fila, etapas);
  const actual = pasos[posicion]!;
  const restan = faltan(fila, etapas);
  const plazo = plazoDe(fila, ahora);
  const desde = enEtapaDesde(fila, ahora);
  const datos = datosDe(fila);
  const temperatura = PIPELINE_COLUMNS.find((c) => c.status === fila.conversacion?.temperatura);
  const nombreDeTemperatura = temperatura?.label ?? 'Sin temperatura';
  const tel = enlaceTel(fila.telefono);
  // De centro a centro de dos nodos seguidos: los nodos van repartidos de borde a borde.
  const tramo = `((100% - var(--nodo)) / ${Math.max(1, pasos.length - 1)})`;

  return (
    <article
      aria-label={`${fila.nombre}: ${actual.etiqueta}`}
      style={{ backgroundImage: FONDO }}
      className={cn(
        'relative flex h-full flex-col gap-5 rounded-[1.75rem] bg-[#070b1f] text-white ring-1 ring-inset ring-white/10',
        compacta
          ? 'p-4 sm:p-5'
          : 'p-5 shadow-[0_22px_44px_-22px_rgba(29,78,216,0.7)] transition-[transform,box-shadow] duration-200 ease-out ' +
              'hover:shadow-[0_28px_52px_-20px_rgba(37,99,235,0.8)] motion-safe:hover:-translate-y-0.5',
      )}
    >
      {/* Si el plazo no cabe al lado del titular, baja a su propia línea (el `grow` desigual lo decide). */}
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="line-clamp-2 min-w-0 grow-[999] basis-64 text-[15px] font-semibold leading-snug">
          <span className="text-sky-300">{actual.etiqueta}</span>
          {actual.sigue && <span className="text-white">{` · ${actual.sigue}`}</span>}
        </p>
        <p data-tono={plazo.tono} className={cn('whitespace-nowrap text-xs font-medium leading-snug', TONO[plazo.tono])}>
          {plazo.texto}
        </p>
      </header>

      <div className="space-y-3">
        {/* `--nodo` es el diámetro de cada nodo: con él se calcula hasta dónde llega la línea encendida. */}
        <div className={cn('relative sm:[--nodo:2rem]', compacta ? '[--nodo:1.5rem]' : '[--nodo:1.75rem]')}>
          <span aria-hidden className="absolute inset-x-0 top-1/2 -mt-[1.5px] h-[3px] rounded-full bg-white/10" />
          {/* Lo recorrido: hasta el centro del paso actual. */}
          <span
            aria-hidden
            style={{ width: `calc(var(--nodo) / 2 + ${posicion} * ${tramo})` }}
            className="absolute left-0 top-1/2 -mt-[1.5px] h-[3px] rounded-full bg-blue-600"
          />
          {/* El tramo luminoso: sale del paso actual y avanza hacia el siguiente. */}
          {restan > 0 && (
            <span
              aria-hidden
              style={{ left: `calc(var(--nodo) + ${posicion} * ${tramo})`, width: `calc(0.6 * (${tramo} - var(--nodo)))` }}
              className="absolute top-1/2 -mt-[1.5px] h-[3px] origin-left rounded-full bg-gradient-to-r from-blue-500 to-sky-300 shadow-[0_0_10px_1px_rgba(56,189,248,0.75)] motion-safe:animate-tramo"
            />
          )}
          <ol aria-label="Pasos del proceso" className="relative flex items-center justify-between">
            {pasos.map((paso, i) => {
              const Icono = ICONOS[paso.icono] ?? Circle;
              const estado = i < posicion ? 'hecho' : i === posicion ? 'actual' : 'falta';
              return (
                <li
                  key={paso.id}
                  title={paso.etiqueta}
                  aria-label={paso.etiqueta}
                  aria-current={estado === 'actual' ? 'step' : undefined}
                  data-estado={estado}
                  className={cn('relative grid aspect-square w-[var(--nodo)] min-w-0 place-items-center rounded-full', NODO[estado])}
                >
                  {/* El paso en que está late mientras quede algo por hacer. */}
                  {estado === 'actual' && restan > 0 && (
                    <span aria-hidden className="absolute inset-0 rounded-full bg-sky-400 opacity-0 motion-safe:animate-latido" />
                  )}
                  <Icono aria-hidden className="relative h-1/2 w-1/2" />
                </li>
              );
            })}
          </ol>
        </div>
        <p className="text-xs text-sky-100/70">
          {restan === 0 ? 'Cerrado' : restan === 1 ? 'Falta 1 paso para cerrar' : `Faltan ${restan} pasos para cerrar`}
          {desde && ` · ${desde}`}
        </p>
      </div>

      {/* Si no caben en una fila, los botones bajan y ocupan todo el ancho (el `grow` desigual lo decide).
          `mt-auto`: en una rejilla, el pie de las tarjetas de una misma fila queda a la misma altura. */}
      <footer className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-3.5">
        <div className="flex min-w-0 grow-[999] basis-48 items-center gap-3">
          <span className="relative shrink-0">
            <Avatar name={fila.nombre} src={fila.avatarUrl} className="h-11 w-11 rounded-2xl" />
            <span
              title={nombreDeTemperatura}
              className={cn('absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[#0a1230]', temperatura?.dot ?? 'bg-slate-400')}
            >
              <span className="sr-only">{nombreDeTemperatura}</span>
            </span>
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold leading-tight text-white">{fila.nombre}</h3>
            {datos.length > 0 && (
              <p className="mt-1 truncate text-xs text-sky-100/75">
                {datos[0]}
                {datos[1] && (
                  <>
                    <span aria-hidden className="mx-1.5 text-white/30">
                      |
                    </span>
                    {datos[1]}
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        {(tel || onAbrirChat || (!compacta && onVerFicha)) && (
          <div className="flex grow gap-2">
            {tel && (
              <a href={tel} className={PILDORA}>
                <Phone aria-hidden className="h-4 w-4" /> Llamar
              </a>
            )}
            {onAbrirChat && (
              <button type="button" onClick={onAbrirChat} className={PILDORA}>
                <MessageCircle aria-hidden className="h-4 w-4" /> Chat
              </button>
            )}
            {!compacta && onVerFicha && (
              <button type="button" onClick={onVerFicha} className={PILDORA}>
                <IdCard aria-hidden className="h-4 w-4" /> Ver ficha
              </button>
            )}
          </div>
        )}
      </footer>
    </article>
  );
}
